import {
  ScreenCardInView,
  ScreenFilterChip
} from 'containers/Intelligence/filters/screenFilterSignature';

import { OrbitEntryContext } from './types';

// The conversation id survives page visits (user decision, PLA-8844 Round 2) but never crosses a
// tenant or a user, so the key is scoped by both. Every access is guarded: Safari private mode and
// a blocked storage quota both throw, and a missing session id must never take the assistant down.

export const ORBIT_SESSION_STORAGE_PREFIX = 'orbit.session';

export interface StoredOrbitSession {
  id: string;
  /**
   * Entry context the session's first turn was created under. The context-conflict card compares it
   * against the live route, so it has to outlive a reload the way the session id does.
   */
  context?: OrbitEntryContext;
  /** The user chose Keep Current Investigation: `ask()` keeps sending `context`, not the live route's. */
  pinned?: boolean;
  /**
   * Identity of the dashboard filters the session was started under, and their human summary. The
   * conflict card compares both against the live screen, so — like `context` — they have to outlive
   * a reload. Client-side only: the PLA-8839 contract has no filter field and none is ever sent.
   */
  filterKey?: string;
  filterLabel?: string;
  /**
   * The cards that were on screen when the session was started, in reading order, for the same
   * comparison. Their own field rather than part of `filterKey` because the two are published at
   * different moments - the filters on the screen's first render, the cards once the scroll observer
   * has settled. Records written before the set replaced the single card carry `cardKey` instead,
   * and are read as a one-card set.
   */
  cardKeys?: string[];
  /**
   * The chips and card titles the session was started under, i.e. what the context row has to draw
   * once the conversation is PINNED to its own context and stops following the live screen. They are
   * stored rather than re-derived because the screen the conversation belongs to may not be the one
   * the reader is on, and only that screen's provider can print its own filter copy (PLA-9563 §1c).
   */
  filterChips?: ScreenFilterChip[];
  cards?: ScreenCardInView[];
  /**
   * The context the user has already answered the conflict card for. Persisted so a reload does not
   * re-ask a decision that was taken before it (PLA-8844 D3).
   */
  dismissedContextKey?: string;
  /**
   * What the session is about, derived from its opening turn. Stored for the same reason `context`
   * is: the launcher's label card names the running investigation while the panel is CLOSED, and
   * the transcript it would otherwise be derived from is fetched only while the panel is open.
   */
  subject?: string;
  /**
   * The turn the latest accepted send created. A follow-up otherwise rewrites the record unchanged,
   * and an identical write fires no `storage` event, so another tab on the conversation would never
   * learn to refetch its transcript.
   */
  latestTurnId?: number;
}

export const orbitSessionStorageKey = (
  tenantId: number | string,
  userId: number | string
): string => `${ORBIT_SESSION_STORAGE_PREFIX}.${tenantId}.${userId}`;

/**
 * The stored card set, migrating a record written when the context was ONE card: its `cardKey`
 * string is read as a one-card set, so a conversation running across the change keeps its baseline
 * instead of losing it and re-adopting whatever is on screen after the reload.
 */
const storedCardKeys = (parsed: {
  cardKey?: unknown;
  cardKeys?: unknown;
}): string[] | undefined => {
  if (Array.isArray(parsed.cardKeys)) {
    const keys = parsed.cardKeys.filter((entry): entry is string => typeof entry === 'string');

    return keys.length === 0 ? undefined : keys;
  }

  return typeof parsed.cardKey === 'string' ? [parsed.cardKey] : undefined;
};

/** The stored chips, dropping anything that is not the full `ScreenFilterChip` shape. */
const storedFilterChips = (value: unknown): ScreenFilterChip[] | undefined => {
  if (!Array.isArray(value)) {
    return undefined;
  }

  const chips = value.filter(
    (entry): entry is ScreenFilterChip =>
      typeof entry?.id === 'string' &&
      typeof entry?.label === 'string' &&
      typeof entry?.value === 'string'
  );

  return chips.length === 0 ? undefined : chips;
};

/** The stored card set with its titles, for the same row. */
const storedCards = (value: unknown): ScreenCardInView[] | undefined => {
  if (!Array.isArray(value)) {
    return undefined;
  }

  const cards: ScreenCardInView[] = value
    .filter(entry => typeof entry?.key === 'string' && typeof entry?.title === 'string')
    .map(entry => ({
      key: entry.key,
      title: entry.title,
      metricId: typeof entry.metricId === 'string' ? entry.metricId : null
    }));

  return cards.length === 0 ? undefined : cards;
};

export const readStoredOrbitSession = (key: string | null): StoredOrbitSession | null => {
  if (key === null) {
    return null;
  }

  try {
    const stored = localStorage.getItem(key);

    if (typeof stored !== 'string' || stored.length === 0) {
      return null;
    }

    // Builds before the conflict card wrote the bare session id. Those are still live sessions, so
    // they are adopted with no context rather than discarded.
    if (!stored.startsWith('{')) {
      return { id: stored };
    }

    const parsed = JSON.parse(stored);

    return typeof parsed?.id === 'string' && parsed.id.length > 0
      ? {
          id: parsed.id,
          context: typeof parsed.context === 'object' ? parsed.context ?? undefined : undefined,
          pinned: parsed.pinned === true,
          subject: typeof parsed.subject === 'string' ? parsed.subject : undefined,
          filterKey: typeof parsed.filterKey === 'string' ? parsed.filterKey : undefined,
          filterLabel: typeof parsed.filterLabel === 'string' ? parsed.filterLabel : undefined,
          cardKeys: storedCardKeys(parsed),
          filterChips: storedFilterChips(parsed.filterChips),
          cards: storedCards(parsed.cards),
          dismissedContextKey:
            typeof parsed.dismissedContextKey === 'string' ? parsed.dismissedContextKey : undefined,
          latestTurnId: typeof parsed.latestTurnId === 'number' ? parsed.latestTurnId : undefined
        }
      : null;
  } catch {
    return null;
  }
};

export const writeStoredOrbitSession = (key: string | null, session: StoredOrbitSession): void => {
  if (key === null) {
    return;
  }

  try {
    localStorage.setItem(key, JSON.stringify(session));
  } catch {
    // Storage is an optimisation, not a requirement: the in-memory session still works.
  }
};

/**
 * Patches one field of the stored record, leaving the rest as written. Used for the subject, which
 * only becomes knowable once the transcript loads — long after the writes that set the id and the
 * context — so re-stating those here would mean re-deriving state this caller does not own.
 * A no-op when no session is stored: there is nothing for the field to belong to.
 */
export const updateStoredOrbitSession = (
  key: string | null,
  patch: Partial<Omit<StoredOrbitSession, 'id'>>
): void => {
  const stored = readStoredOrbitSession(key);

  if (stored === null) {
    return;
  }

  writeStoredOrbitSession(key, { ...stored, ...patch });
};

export const clearStoredOrbitSession = (key: string | null): void => {
  if (key === null) {
    return;
  }

  try {
    localStorage.removeItem(key);
  } catch {
    // Nothing to recover: a key that cannot be removed also could not have been written.
  }
};
